import { post } from "../../../../static/assets/utilities/ajax.js";
import './jquery-counter.js';
import bootstrapcomponent from './../../../../static/assets/js/bootstrap-component.js';

/**
 * Main class for handling slick slider functionality
 */
class Slick_General {
    /**
     * @private
     * @type {bootstrapcomponent}
     */
    #bootstrap;

    /**
     * @private
     * Default settings for sliders
     */
    #defaultSettings = {
        rtl: document.documentElement.getAttribute('dir') === 'rtl',
        lazyLoad: 'ondemand',
        slidesToShow: 4,
        slidesToScroll: 1,
        infinite: false,
        arrows: true,
        dots: false,
        draggable: true,
        nextArrow: '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>',
        prevArrow: '<button class="PreArrow-two"><i class="icon-arrow-left"></i></button>',
        responsive: [
            { breakpoint: 1368, settings: { slidesToShow: 3 } },
            { breakpoint: 1025, settings: { slidesToShow: 3 } },
            { breakpoint: 993, settings: { slidesToShow: 2 } },
            { breakpoint: 577, settings: { slidesToShow: 1 } }
        ]
    };

    /**
     * @private
     * Configuration for selectors and classes
     */
    #config = {
        selectors: {
            episodeSlider: '.init-episode-slider',
            changeSeason: '.css_prefix-change-season',
            continueWatching: '.continue_watch_empty',
            themeDirection: 'input[name="theme_scheme_direction"]',
            slickGeneral: '.css_prefix-slick-general',
            verticalBanner: '.css_prefix-vertical-thumbnail-banner',
            mainBanner: '.css_prefix-main-banner',
            simpleBanner: '.css_prefix-simple-banner',
            tvShowSeason: '.css_prefix-tvshow-season',
            tvShowTab: '.css_prefix-tvshow-tab',
            productBanner: '.css_prefix-product-banner',
            countdown: '.st-count-down'
        },
        classes: {
            slickInitialized: 'slick-initialized',
            active: 'active',
            first: 'first',
            last: 'last',
            clonedItem: 'cloned-item'
        }
    };

    /**
     * @private
     * Cache storage for performance optimization
     */
    #cache = new Map();

    /**
     * Constructor
     */
    constructor() {
        try {
            this.#bootstrap = new bootstrapcomponent();
            this.#init();
            this.#initPerformanceMonitoring();
        } catch (error) {
            this.#handleError(error, 'Constructor initialization failed');
        }
    }

    /**
     * @private
     * Initialize all required functionality
     */
    #init() {
        this.#initializeComponents();
        this.#bindEvents();
    }

    /**
     * @private
     * Initialize performance monitoring
     */
    #initPerformanceMonitoring() {
        if (typeof performance === 'undefined') return;

        this.#measurePerformance('init', () => {
            this.#initializeComponents();
        });
    }

    /**
     * @private
     * Measure performance of a function
     * @param {string} label - Performance measurement label
     * @param {Function} fn - Function to measure
     */
    #measurePerformance(label, fn) {
        const start = performance.now();
        fn();
        const duration = performance.now() - start;
    }

    /**
     * @private
     * Initialize all components
     */
    #initializeComponents() {
        try {
            this.sliderElement('frontend');
            this.sliderElement('editor');
            this.sliderDetailsPage();
            this.hoverEffect();
            this.setInitialDirection();
        } catch (error) {
            this.#handleError(error, 'Component initialization failed');
        }
    }

    /**
     * @private
     * Handle errors consistently
     * @param {Error} error - Error object
     * @param {string} context - Error context
     */
    #handleError(error, context) {
        console.error(`Error: ${context}`, {
            message: error.message,
            stack: error.stack
        });

        // Show user-friendly error message
        this.#showErrorMessage('An error occurred. Please try again.');
    }

    /**
     * @private
     * Show error message to user
     * @param {string} message - Error message
     */
    #showErrorMessage(message) {
        jQuery('<div>')
            .addClass('error-message')
            .text(message)
            .appendTo('body')
            .fadeIn()
            .delay(3000)
            .fadeOut(() => jQuery(this).remove());
    }

    /**
     * @private
     * Bind all event handlers
     */
    #bindEvents() {
        try {
            const { body } = document;
            const { selectors } = this.#config;

            // Debounced resize handler
            const debouncedHoverEffect = this.#debounce(() => this.hoverEffect(), 250);
            jQuery(window).on('resize', debouncedHoverEffect);

            // Event delegation
            jQuery(body)
                .on('change', selectors.themeDirection, this.#handleDirectionChange.bind(this))
                .on('click', selectors.episodeSlider, this.#handleEpisodeSlider.bind(this))
                .on('click', selectors.changeSeason, this.#handleSeasonChange.bind(this))
                .on('click', selectors.continueWatching, this.#handleRemoveContinueWatching.bind(this));
        } catch (error) {
            this.#handleError(error, 'Event binding failed');
        }
    }

    /**
     * @private
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} Debounced function
     */
    #debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * @private
     * Update the direction of the page and sliders
     * @param {string} direction - New direction (ltr/rtl)
     */
    #updateDirection(direction) {
        try {
            // Update HTML direction attribute
            jQuery("html").attr("dir", direction);

            // Save the direction preference
            this.#setCookie("theme_scheme_direction", direction, 7);

            // Update UI to reflect direction change
            jQuery(`#theme-scheme-direction-${direction}`).addClass("active");
            jQuery(`#theme-scheme-direction-${direction === "ltr" ? "rtl" : "ltr"}`).removeClass("active");

            // Reinitialize sliders with new direction
            this.reinitializeAllSliders();
            this.reinitsliderDetailsPage();
        } catch (error) {
            this.#handleError(error, 'Failed to update direction');
        }
    }

    /**
     * @private
     * @param {Event} event - Direction change event
     */
    #handleDirectionChange(event) {
        try {
            const selectedDir = jQuery(event.target).val();
            this.#bootstrap.closeVisibleOffcanvas('#rtl-switcher-options');
            this.#updateDirection(selectedDir);
        } catch (error) {
            this.#handleError(error, 'Direction change handling failed');
        }
    }

    /**
     * @private
     * Handle episode slider click event
     */
    #handleEpisodeSlider(event) {
        this.InitEpisodeSlider(event);
    }

    /**
     * @private
     * Handle season change click event
     */
    #handleSeasonChange(event) {
        this.GetSeasonEpisodes(event);
    }

    /**
     * @private
     * Handle remove continue watching click event
     */
    #handleRemoveContinueWatching(event) {
        this.RemoveContinueWatching(event);
    }

    /**
     * @private
     * @param {string} direction - New direction (ltr/rtl)
     */
    #setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 864e5)); // Days to milliseconds
        document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`;
    }

    /**
     * @private
     * @param {string} name - Cookie name
     * @returns {string|null} Cookie value
     */
    #getCookie(name) {
        return document.cookie.split('; ').reduce((acc, cookie) => {
            const [key, val] = cookie.split('=');
            return key === name ? val : acc;
        }, null);
    }

    /**
     * @private
     * Set initial direction based on saved cookie or default
     */
    setInitialDirection() {
        const htmlTag = jQuery("html");
        const initialDir = htmlTag.attr("dir") || "ltr";
        const savedDir = this.#getCookie("theme_scheme_direction") || initialDir;

        jQuery(`#theme-scheme-direction-${savedDir}`).addClass("active");
        jQuery(`#theme-scheme-direction-${savedDir === "ltr" ? "rtl" : "ltr"}`).removeClass("active");
    }

    /**
     * @private
     * Initialize slider elements based on mode
     * @param {string} mode - Mode (frontend/editor)
     */
    sliderElement(mode) {
        try {
            const initEvent = mode === 'frontend' ? 'elementor/frontend/init' : 'elementor/editor/init';
            const hook = mode === 'frontend' ? 'frontend/element_ready/widget' : 'frontend/element_ready/widget';

            this.#measurePerformance(`sliderElement-${mode}`, () => {
                if (window.addEventListener) {
                    window.addEventListener(initEvent, () => {
                        if (mode === 'editor') {
                            if (window.elementor) {
                                elementor.on('preview:loaded', () => {
                                    this.addElementorHook(hook);
                                });
                            }
                        } else {
                            this.addElementorHook(hook);
                        }
                    });
                }
            });
        } catch (error) {
            this.#handleError(error, `Slider element initialization failed for mode: ${mode}`);
        }
    }

    /**
     * @private
     * Initialize slider details page
     */
    sliderDetailsPage() {
        const sliders = jQuery('.single_page_slick');
        if (sliders.length > 0) {
            sliders.each((index, element) => {
                const sliderElement = jQuery(element).find('.css_prefix-slick-general');
                if (sliderElement.length > 0 && !sliderElement.hasClass('slick-initialized')) {
                    this.initSlickSlider(sliderElement, this);
                }
            });
        }
    }

    /**
     * @private
     * Reinitialize slider details page
     */
    reinitsliderDetailsPage() {
        const sliders = jQuery('.single_page_slick');

        if (sliders.length > 0) {
            sliders.each((index, element) => {
                const sliderElement = jQuery(element).find('.css_prefix-slick-general');

                if (sliderElement.length > 0) {
                    // Unslick if already initialized
                    if (sliderElement.hasClass('slick-initialized')) {
                        sliderElement.slick('unslick'); // Unslick the slider
                    }

                    // Re-initialize the Slick slider
                    this.initSlickSlider(sliderElement, this);
                }
            });
        }
    }

    /**
     * @private
     * Reinitialize all sliders
     */
    reinitializeAllSliders() {
        const sliderTypes = [
            { selector: '.css_prefix-slick-general', initMethod: this.initSlickSlider },
            { selector: '.css_prefix-vertical-thumbnail-banner', initMethod: this.initVTBannerSlider },
            { selector: '.css_prefix-main-banner', initMethod: this.initBannerSlider },
            { selector: '.css_prefix-simple-banner', initMethod: this.initSlickSlider },
            { selector: '.css_prefix-tvshow-season', initMethod: this.initTvShowSeason, extraMethods: [this.ajaxChangeSeason] },
            { selector: '.css_prefix-tvshow-tab', initMethod: this.tvShowTabSlider, extraMethods: [this.tvShowSeasonChange] },
            { selector: '.css_prefix-product-banner', initMethod: this.initSlickSlider },
            { selector: '.st-count-down', initMethod: this.callTimer }
        ];

        sliderTypes.forEach(({ selector, initMethod, extraMethods = [] }) => {
            const elements = jQuery(selector);

            // Ensure we're handling each element separately
            elements.each((index, element) => {
                const $element = jQuery(element);

                // Unslick if initialized
                if ($element.hasClass('slick-initialized')) {
                    $element.slick('unslick');
                }

                // Reinitialize the slider
                this.initializeSlider($element, initMethod, extraMethods);
            });
        });
    }

    /**
     * @private
     * Add Elementor hook for slider initialization
     * @param {string} hook - Elementor hook
     */
    addElementorHook(hook) {
        const sliderTypes = [
            { selector: '.css_prefix-slick-general', initMethod: this.initSlickSlider },
            { selector: '.css_prefix-vertical-thumbnail-banner', initMethod: this.initVTBannerSlider },
            { selector: '.css_prefix-main-banner', initMethod: this.initBannerSlider },
            { selector: '.css_prefix-simple-banner', initMethod: this.initSlickSlider },
            { selector: '.css_prefix-tvshow-season', initMethod: this.initTvShowSeason, extraMethods: [this.ajaxChangeSeason] },
            { selector: '.css_prefix-tvshow-tab', initMethod: this.tvShowTabSlider, extraMethods: [this.tvShowSeasonChange] },
            { selector: '.css_prefix-product-banner', initMethod: this.initSlickSlider },
            { selector: '.st-count-down', initMethod: this.callTimer }
        ];
        if (typeof elementorFrontend !== 'undefined') {
            elementorFrontend.hooks.addAction(hook, ($scope) => {
                sliderTypes.forEach(({ selector, initMethod, extraMethods = [] }) => {
                    const elements = $scope.find(selector);
                    this.initializeSlider(elements, initMethod, extraMethods);
                });
            });
        }
    }

    /**
     * @private
     * Initialize slider
     * @param {jQuery} elements - Slider elements
     * @param {Function} initMethod - Initialization method
     * @param {Array<Function>} extraMethods - Extra methods to call after initialization
     */
    initializeSlider(elements, initMethod, extraMethods = []) {
        if (elements.length > 0 && !elements.hasClass('slick-initialized')) {
            // Initialize the slider with the initMethod
            initMethod.call(this, elements, this);

            // Call extra methods after slider initialization
            extraMethods.forEach(method => {
                // Ensure each extraMethod gets the correct context and necessary arguments
                method.call(this, elements, this); // Pass elements and `this` context if needed
            });
        }
    }

    /**
     * Handles the logic for changing TV show seasons, including initializing sliders,
     * populating episode data, and updating content based on season selection.
     */
    tvShowSeasonChange(args = {}) {
        const is_rtl = document.documentElement.getAttribute('dir') === 'rtl';

        /**
         * Initializes the Slick Slider for the episodes carousel.
         * @param {jQuery} sliderTarget - The jQuery object of the slider element.
         */
        const initializeSlider = (sliderTarget) => {
            // Initialize the slider only if it hasn't been initialized already
            if (!sliderTarget.hasClass('slick-initialized')) {
                sliderTarget.slick({
                    rtl: is_rtl,
                    slidesToShow: 4,
                    slidesToScroll: 1,
                    infinite: false,
                    arrows: false,
                    dots: true,
                    draggable: true,
                    nextArrow: '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>',
                    prevArrow: '<button class="PrevArrow-two"><i class="icon-arrow-left"></i></button>',
                    responsive: [
                        { breakpoint: 1368, settings: { slidesToShow: 3 } },
                        { breakpoint: 1025, settings: { slidesToShow: 3 } },
                        { breakpoint: 993, settings: { slidesToShow: 2 } },
                        { breakpoint: 577, settings: { slidesToShow: 1 } }
                    ],
                });
            }
        };

        /**
         * Populates the slider with episode content retrieved from the response.
         * @param {jQuery} sliderSelector - The jQuery selector for the slider.
         * @param {Object} json_res - The JSON response containing episode data.
         * @returns {number} - The number of updated items in the slider.
         */
        const populateSliderContent = (sliderSelector, json_res) => {
            let length = 0;

            // Iterate through the existing slides and populate content
            sliderSelector.find('.slick-slide').each((key, value) => {
                const $slide = jQuery(value);
                if (typeof json_res['result'][key] === 'undefined') {
                    $slide.remove(); // Remove any extra items
                    return;
                }
                // Set the block content for each episode
                $slide.find('.episode-card').html(json_res['result'][key]['block_main_content'])
                    .removeClass('skeleton').addClass('animate');
                length = key + 1; // Track how many items were updated
            });
            return length; // Return the count of updated items
        };

        /**
         * Adds any remaining episodes to the slider.
         * @param {jQuery} sliderSelector - The jQuery selector for the slider.
         * @param {Object} json_res - The JSON response containing episode data.
         * @param {number} startIndex - The index at which to start adding episodes.
         */
        const addRemainingEpisodes = (sliderSelector, json_res, startIndex) => {
            // Loop through remaining episodes and add them to the slider
            for (let index = startIndex; index < json_res['result'].length; index++) {
                const element = json_res['result'][index];
                const tempElement = jQuery('<div>').html(element['block_main_content']);
                // Extract the image and description
                const block_img = tempElement.find('.episode-image').html(); // Gets full image div
                const block_contnt = tempElement.find('.episode-detail').html(); // Gets episode details
                const cloned_item = sliderSelector.find('.episode-item').last().clone(true, true);
                cloned_item.find('.episode-image').empty().append(block_img);
                cloned_item.find('.episode-detail').empty().append(block_contnt);

                // Add the cloned item to the slider
                sliderSelector.slick('slickAdd', cloned_item);
            }
        };

        // Event delegation for the season select change event
        jQuery(document.body).on('change', '.season-select', function () {
            const btn = jQuery(this);
            const episode = btn.parents('.trending-custom-tab').find('.css_prefix-tv_show-episodes').data('episodes');
            const season_no = btn.val(); // Get the selected season number
            const target = btn.parents('.trending-custom-tab').find('.episodes-contens');

            // Check if the content for the selected season is already loaded
            if (target.children(`[data-display="${season_no}"]`).length) {
                jQuery('.episodes-slider').hide();
                jQuery(`.episodes-slider[data-display="${season_no}"]`).show();
                return;
            }

            const episode_count = episode[season_no]?.episodes?.length || 0;

            // Generate the HTML for the new slider based on episode count
            let sliderHTML = `<div class="episodes-slider slick-slider-tvshow-tab ajax-slick-load" data-display="${season_no}">`;

            for (let i = 0; i < episode_count; i++) {
                sliderHTML += '<div class="episode-item animated fadeInUp ajax"><div class="episode-card"></div></div>';
            }
            sliderHTML += '</div>';
            target.append(sliderHTML);

            // Hide all sliders initially, then show the one for the selected season
            jQuery('.episodes-slider').hide();
            jQuery(`.episodes-slider[data-display="${season_no}"]`).show();

            // Make the API call to get the season data
            post('tvshow_tab_seasons_data', { data: episode, season: season_no })
                .then(res => {
                    let json_res = JSON.parse(res);

                    if (json_res['success']) {
                        const sliderSelector = target.find(`.episodes-slider[data-display="${season_no}"]`);
                        initializeSlider(sliderSelector); // Initialize the slider
                        const length = populateSliderContent(sliderSelector, json_res);
                        addRemainingEpisodes(sliderSelector, json_res, length); // Add remaining episodes
                    }
                });
        });

        // Event delegation for the episode button click (one-time click)
        jQuery(document.body).on('click', '.css_prefix-tv_show-episodes', function () {
            const btn = jQuery(this);
            const season_no = 0; // Default season number
            const episode = btn.data('episodes');
            const target = btn.closest('.trending-custom-tab').find('.episodes-contens');

            const sliderTarget = target.find('.episodes-slider');

            // Make the API call to get season data
            post('tvshow_tab_seasons_data', { data: episode, season: season_no })
                .then(res => {
                    let json_res = JSON.parse(res); // Parse the response
                    if (json_res['success']) {
                        // Initialize the slider and populate content
                        initializeSlider(sliderTarget);
                        const length = populateSliderContent(sliderTarget, json_res);
                        addRemainingEpisodes(sliderTarget, json_res, length);
                    }
                });
        });
    }

    /**
     * Initialize TV show tab slider
     * @param {Object} args - Arguments
     */
    tvShowTabSlider(args = {}) {
        const isRTL = document.documentElement.getAttribute('dir') === 'rtl';

        jQuery('.css_prefix-tvshow-tab').each(function () {
            const $this  = jQuery(this);
            const mainID = '#' + $this.find('.trending-slider-tab').attr('data-rand');
            const navID  = '#' + $this.find('.trending-slider-tab-nav').attr('data-rand');
            const $main  = jQuery(mainID);
            const $nav   = jQuery(navID);

            // Clean up existing sliders
            [$main, $nav].forEach($el => $el.hasClass('slick-initialized') && $el.slick('unslick'));

            // Toggle single slide class
            $main.toggleClass('single-slide', $main.children().length === 1);

            // Main slider with afterChange event
            $main.on('afterChange', (_, slick, slide) => {
                const randID = slick.$slider.data('rand_id');
                const num = slide + 1;
                const tab = jQuery('#episodes_' + num + randID);
                if (!tab.hasClass('active show')) {
                    jQuery('.streamit-trending-pills .nav-link[href="#overview_' + num + randID + '"]').addClass('active');
                    jQuery('#overview_' + num + randID).addClass('active show');
                }
            }).slick({
                lazyLoad: 'ondemand',
                rtl: isRTL,
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                fade: true,
                draggable: true,
                infinite: true,
                swipe: false,
                touchMove: false,
                asNavFor: navID,
                nextArrow: '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>',
                prevArrow: '<button class="PrevArrow-two"><i class="icon-arrow-left"></i></button>'
            });

            // Nav slider
            const navCount = $nav.children().length;
            if (navCount) {
                $nav.slick({
                    rtl: isRTL,
                    lazyLoad: 'ondemand',
                    slidesToShow: 5,
                    slidesToScroll: 1,
                    asNavFor: mainID,
                    dots: false,
                    arrows: false,
                    focusOnSelect: true,
                    centerPadding: '0',
                    infinite: true,
                    centerMode: navCount > 5,
                    responsive: [
                        { breakpoint: 1024, settings: { slidesToShow: 3, centerMode: navCount > 3 } },
                        { breakpoint: 768, settings: { slidesToShow: 3, centerMode: navCount > 3 } },
                        { breakpoint: 401, settings: { slidesToShow: 3, centerMode: navCount > 3 } }
                    ]
                });
            }
        });
    }

    /**
     * Fetches TV show seasons data and dynamically updates the season and episode content.
     * @param {Object} data - The data to be sent with the AJAX request.
     * @param {jQuery} $this - The jQuery object of the element triggering the AJAX call.
     * @param {number} first_tvshow_id - The ID of the first TV show for comparison.
     */
    ajaxCall(data, $this, first_tvshow_id) {
        // Get the closest parent element that contains the TV show season data
        const mainParent = $this.closest(".css_prefix-tvshow-season-parent");

        // Make an AJAX post request to fetch the TV show seasons data
        post('tvshow_seasons_data', data)
            .then(res => {
                // Parse the response data
                const json_res = res.data;

                // Check if the response is a valid array and has data
                if (Array.isArray(json_res) && json_res.length > 0) {
                    // Check if new content is available before clearing existing data
                    const hasSeasonContent = json_res.some(value => value['season_content']);
                    const hasBlockContent = json_res.some(value => value['block_content']);
                    const hasButtonContent = json_res.some(value => value['button_content']);

                    // Clear existing data only if new content is available
                    if (hasSeasonContent) {
                        mainParent.find('.nav-tabs-inner').empty(); // Clear seasons
                    }
                    if (hasBlockContent) {
                        mainParent.find('.season-episodes-details').empty(); // Clear episodes
                    }
                    if (hasButtonContent) {
                        mainParent.find('.view-all-btn-parent').empty(); // Clear "view all" button
                    }

                    // Hide existing episodes meta if tvshow_id in the response does not match the first TV show ID
                    if (json_res[0]['tvshow_id'] !== undefined && json_res[0]['tvshow_id'] !== first_tvshow_id) {
                        mainParent.find('.nav-tabs-inner .css_prefix-episodes-meta').hide();
                    }

                    // Loop through the response and append season and episode content
                    json_res.forEach(value => {
                        // Check and append season content if exists
                        if (value['season_content']) {
                            mainParent.find('.nav-tabs-inner').append(value['season_content']);
                            // Set the first season as active and mark it as loaded
                            mainParent.find('.tvshow-' + json_res[0]['tvshow_id']).first()
                                .addClass('active')
                                .data('ajax_loaded', true);
                        }

                        // Append block content for episodes if available
                        if (value['block_content']) {
                            mainParent.find('.season-episodes-details').append(value['block_content']);
                        }

                        // Append the "view all" button if it exists; otherwise, hide the button container
                        if (value['button_content']) {
                            mainParent.find('.view-all-btn-parent').append(value['button_content']);
                        } else {
                            mainParent.find('.view-all-btn-parent').hide();
                        }
                    });

                    // Mark the button as loaded and set the active class for the first TV show tab
                    $this.data('ajax_loaded', true);
                    mainParent.find('.tvshow-' + json_res[0]['tvshow_id']).first()
                        .addClass("active")
                        .data('ajax_loaded', true);

                    // Remove the skeleton loader once content is loaded
                    mainParent.find('.skeleton-box').remove();
                } else {
                    // Handle case when no episodes are found in the response
                    mainParent.find('.season-episodes-details').append("<div class='episodes-info'>No Episodes Found</div>");
                }
            })
            .catch(err => {
                // Log error if the AJAX request fails
                console.error("Error fetching TV show seasons data:", err);
            });
    }

    /**
     * Initializes the TV Show Season Slick Slider with advanced configurations.
     * @param {jQuery} sliderElement - The jQuery element representing the slider.
     */
    initTvShowSeason(sliderElement, args = {}) {
        // Get slider settings and extra settings from data attributes
        const slickSettings = sliderElement.data('slider_settings');
        const extraSettings = sliderElement.data('extra_settings');
        const is_rtl = document.documentElement.getAttribute('dir') === 'rtl';

        // Add extra settings if necessary
        if (extraSettings === true) {
            slickSettings.nextArrow = '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>';
            slickSettings.prevArrow = '<button class="PreArrow-two"><i class="icon-arrow-left"></i></button>';
        }

        // If slickSettings is not defined, exit early
        if (!slickSettings) {
            return;
        }
        slickSettings.rtl = is_rtl;
        slickSettings.lazyLoad = 'ondemand';

        // Cache repeated selectors to avoid querying DOM multiple times
        const detailWrap = sliderElement.closest('.css_prefix-wrap-details');
        const episodeCardText = detailWrap.find('.p-btns').data('episode_card_text');
        const navTabsInner = detailWrap.find('.nav-tabs-inner');
        const firstTvShowId = navTabsInner.data('first_tvshow_id');

        // Initialize the slider with the defined settings
        jQuery(sliderElement)
            .on('afterChange', (event, slick, currentSlide) => {
                // Get the current slide element and its associated data
                const element = jQuery(sliderElement).find(`[data-slick-index=${currentSlide}]`);
                const tvShowId = element.data("post");

                // Remove the active class from all nav-tabs-inner links
                navTabsInner.find("a").removeClass('active');

                // Check if the element's data has already been loaded
                const data = {
                    'tvshow_id': tvShowId,
                    'season_change': true,
                    'season_number': 0, // Update this dynamically if needed
                    'episode_card_text': episodeCardText,
                    'first_tvshow_id': firstTvShowId
                };

                // Call the AJAX function for loading data, use arrow function to maintain `this`
                this.ajaxCall(data, element, firstTvShowId, currentSlide);

                // Hide all details initially
                detailWrap.find('.season-episodes-details .episodes-info').hide();
                detailWrap.find('.css_prefix-episodes-meta').hide();

                // Select and show the active seasons and episodes based on TV show ID
                const showSeasonsSelector = `.tvshow-${tvShowId}`;
                const showEpisodesSelector = `.tvshow-${tvShowId}-season-1`; // You can update the season number dynamically

                detailWrap.find(showSeasonsSelector).first().addClass('active').show();
                detailWrap.find(showEpisodesSelector).show();
            })
            .slick(slickSettings);
    }

    /**
     * Handles season change via AJAX when an episode meta button is clicked.
     */
    ajaxChangeSeason() {
        // Use event delegation for dynamically loaded elements
        jQuery(document).on("click", '.css_prefix-episodes-meta', (event) => {
            const $this = jQuery(event.currentTarget);
            const parent = $this.closest('.css_prefix-wrap-details');

            // Cache commonly accessed elements
            const episodeCardText = parent.find('.p-btns').data('episode_card_text');
            const tvShowId = $this.data('tvshow-id');
            const seasonNumber = parseInt($this.data('season'), 10);
            const ajaxLoaded = $this.data('ajax_loaded');

            // Determine the next season
            const nextSeasonNumber = seasonNumber + 1;
            const $showSeason = `.tvshow-${tvShowId}-season-${nextSeasonNumber}`;

            // Clear active class and hide current season details
            parent.find('.css_prefix-episodes-meta').removeClass('active');
            parent.find('.season-episodes-details .episodes-info').hide();

            // Mark the clicked season as active
            $this.addClass('active');

            // Show the new season's details and apply fade-in animation
            jQuery($showSeason).show().addClass("fade-in-anim");

            // If the episodes info has the fade-in-anim class, remove it
            parent.find('.episodes-info').removeClass("fade-in-anim");

            // Prepare the AJAX data to load the new season's content
            const data = {
                'action': 'tvshow_seasons_data',
                'tvshow_id': tvShowId,
                'season_change': false,
                'season_number': seasonNumber,
                'episode_card_text': episodeCardText,
            };

            // Only trigger AJAX call if the data isn't already loaded
            this.ajaxCall(data, $this, seasonNumber);
        });
    }

    /**
     * Initializes the vertical banner slider with synchronized main and thumbnail sliders.
     * @param {jQuery} vtbanner - The vertical banner container element(s) to initialize the slider for.
     */
    initVTBannerSlider(vtbanner, args = {}) {
        const is_rtl = document.documentElement.getAttribute('dir') === 'rtl';
        const $this = jQuery(vtbanner);
        const $verticalBannerContent = $this.find('.vertical-banner-content');
        const $verticalBannerThumb = $this.find('.vertical-banner-thumb');

        // Check if the slick slider is already initialized
        if ($verticalBannerContent.hasClass('slick-initialized')) {
            $verticalBannerContent.slick('unslick');
        }
        if ($verticalBannerThumb.hasClass('slick-initialized')) {
            $verticalBannerThumb.slick('unslick');
        }

        // Get unique random IDs for each slider
        const verticalBannerContentId = '#' + $verticalBannerContent.attr('data-rand');
        const verticalBannerThumbId = '#' + $verticalBannerThumb.attr('data-rand');

        // Get parent wrappers for arrows
        const $arrowForVertical = $this.find('.vertical-banner-thumb-wrapper');
        const $arrowParent = $this.find('.vertical-banner-content');

        // Arrow buttons for both sliders
        const nextArrow = '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>';
        const prevArrow = '<button class="PreArrow-two"><i class="icon-arrow-left"></i></button>';

        // Initialize the main content slider
        jQuery(verticalBannerContentId).slick({
            rtl: is_rtl,
            slidesToShow: 1,
            arrows: true,
            fade: true,
            lazyLoad: 'ondemand',
            asNavFor: verticalBannerThumbId,
            appendArrows: $arrowForVertical,
            nextArrow: nextArrow,
            prevArrow: prevArrow,
            responsive: [{
                breakpoint: 992,
                settings: {
                    asNavFor: false,
                    nextArrow: nextArrow,
                    prevArrow: prevArrow,
                }
            }]
        });

        // Initialize the thumbnail slider
        jQuery(verticalBannerThumbId).slick({
            slidesToShow: 3,
            asNavFor: verticalBannerContentId,
            dots: false,
            arrows: true,
            infinite: true,
            vertical: true,
            verticalSwiping: true,
            centerMode: true,
            lazyLoad: 'ondemand',
            appendArrows: $arrowParent,
            nextArrow: nextArrow,
            prevArrow: prevArrow,
            focusOnSelect: true,
        });

        // Force Slick to update after initialization
        setTimeout(() => {
            jQuery(verticalBannerContentId).slick('setPosition');
            jQuery(verticalBannerThumbId).slick('setPosition');
        }, 300);
    }

    /**
     * Initializes the banner slider with slick carousel, including thumbnail navigation.
     * @param {jQuery} bannerSlider - The banner slider container element to initialize.
     * @param {Object} args - Optional arguments (e.g., RTL setting).
     */
    initBannerSlider(bannerSlider, args = {}) {
        const is_rtl = document.documentElement.getAttribute('dir') === 'rtl';
        const $this = bannerSlider; // The main slider element

        // Find the unique selector for the main slider
        const mainSliderSelector = '#' + $this.attr('id');

        // Find the thumbnail navigation slider element and its unique selector
        const $thumbSliderNav = $this.closest(".st-main-slider").find('.banner-thumb-slider-nav');
        const thumbSliderNavSelector = '#' + $thumbSliderNav.data('rand');

        // Unslick both sliders if they are already initialized.
        // This makes the function safe for re-initialization on RTL/LTR switch.
        if (jQuery(mainSliderSelector).hasClass('slick-initialized')) {
            jQuery(mainSliderSelector).slick('unslick');
        }
        if (jQuery(thumbSliderNavSelector).hasClass('slick-initialized')) {
            jQuery(thumbSliderNavSelector).slick('unslick');
        }

        // Get Slick settings from data attributes
        const slickSettings = $this.data('slider_settings') || {};
        const slickChildSettings = $this.data('slick_child_settings') || {};

        // Define arrow buttons for the thumbnail slider
        const nextArrow = '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>';
        const prevArrow = '<button class="PreArrow-two"><i class="icon-arrow-left"></i></button>';

        // Configure settings for the main slider
        slickSettings.asNavFor = thumbSliderNavSelector;
        slickSettings.rtl = is_rtl;
        slickSettings.lazyLoad = 'ondemand';

        // Configure settings for the thumbnail slider
        slickChildSettings.asNavFor = mainSliderSelector;
        slickChildSettings.nextArrow = nextArrow;
        slickChildSettings.prevArrow = prevArrow;
        slickChildSettings.rtl = is_rtl;
        slickChildSettings.lazyLoad = 'ondemand';

        // Re-initialize the main banner slider
        if (jQuery(mainSliderSelector).length > 0) {
            jQuery(mainSliderSelector).slick(slickSettings);
        }

        // Re-initialize the thumbnail navigation slider
        if (jQuery(thumbSliderNavSelector).length > 0) {
            jQuery(thumbSliderNavSelector).slick(slickChildSettings);
        }
    }

    /**
     * Initializes a Slick slider with custom settings and applies 'first' and 'last' classes to active slides.
     * @param {jQuery} sliderElement - The slider element to initialize.
     */
    initSlickSlider(sliderElement, args = {}) {
        // Check if the slider has already been initialized
        if (!sliderElement.hasClass('slick-initialized')) {
            const is_rtl = document.documentElement.getAttribute('dir') === 'rtl';

            let slickSettings = sliderElement.data('slider_settings');
            slickSettings.lazyLoad = 'ondemand';
            // Use slingle_slider_settings if defined and non-empty
            const singleSliderSettings = sliderElement.data('slingle_slider_settings');
            if (singleSliderSettings) {
                slickSettings = singleSliderSettings;
            }

            // Apply extra settings for navigation arrows
            const extraSettings = sliderElement.data('extra_settings');

            if (extraSettings === true) {
                slickSettings.nextArrow = '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>';
                slickSettings.prevArrow = '<button class="PreArrow-two"><i class="icon-arrow-left"></i></button>';
            }

            slickSettings.rtl = is_rtl;

            // Initialize the slider if settings are available
            if (slickSettings) {
                // Initialize the Slick slider with the provided setting
                sliderElement.slick(slickSettings);

                // Cache slide items and active slide elements
                const slideItems = sliderElement.find('.slick-slide');
                const activeSlides = sliderElement.find('.slick-active');

                // Add "first" and "last" classes to the initially active slides
                activeSlides.first().addClass('first');
                activeSlides.last().addClass('last');

                // After the slide changes, update the "first" and "last" classes
                sliderElement.on('afterChange', function () {
                    // Remove "first" and "last" from all slides
                    slideItems.removeClass('first last');

                    // Get the new active slide
                    const newActiveSlides = sliderElement.find('.slick-active');

                    // Add "first" and "last" classes to the new active slide
                    newActiveSlides.first().addClass('first');
                    newActiveSlides.last().addClass('last');
                });
            }
        }
    }

    /**
     * Updates 'first' and 'last' classes for the active slides after a change in the slider.
     * @param {jQuery} sliderElement - The slider element to update.
     */
    updateFirstLastClassesAfterChange(sliderElement) {
        const slideItems = sliderElement.find(".slick-track .slick-slide");
        const activeSlides = sliderElement.find(".slick-active");

        // Remove 'first' and 'last' classes from all slides
        slideItems.removeClass("first last");

        // Add 'first' and 'last' classes to the active slides
        activeSlides.first().addClass("first");
        activeSlides.last().addClass("last");
    }

    /**
     * Initializes the 'first' and 'last' classes for the active slides when the slider is initialized.
     * @param {jQuery} sliderElement - The slider element to initialize.
     */
    updateFirstLastClassesInit(sliderElement) {
        const activeSlides = sliderElement.find('.slick-active');

        // Add 'first' and 'last' classes to the initially active slides
        activeSlides.first().addClass("first");
        activeSlides.last().addClass("last");
    }

    /**
     * Initializes a countdown timer for a given element.
     * @param {HTMLElement} element - The element containing the countdown data attributes.
     */
    callTimer(element, args = {}) {
        const $element = jQuery(element);
        const futureDate = $element.attr('data-date');
        const label = $element.attr('data-labels') === "true";
        const displayFormat = $element.attr('data-format');

        // Initialize the countdown timer
        $element.countdowntimer({
            dateAndTime: futureDate,
            labelsFormat: label,
            displayFormat: displayFormat,
        });
    }

    /**
     * Applies hover effects and attaches watchlist update listeners.
     */
    hoverEffect() {
        // Skip hover effects on small screens
        if (jQuery(window).width() <= 767) return;

        let hoverTimeout; // Store timeout for removing the cloned item
        let hoverDelayTimeout; // Timeout for adding delay before showing hover effect

        jQuery('.display-hover-effect').hover(
            function (e) {
                // Clear any existing timeouts
                clearTimeout(hoverTimeout);
                clearTimeout(hoverDelayTimeout);

                const _this = jQuery(e.currentTarget);

                // Add a delay before triggering the hover effect
                hoverDelayTimeout = setTimeout(() => {
                    try {
                        const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
                        const $hiddenData = _this.find('.hidden-hover-data');

                        // Ensure hidden data exists
                        if ($hiddenData.length === 0) return;

                        // Clone the hidden hover data and create the hover effect
                        const $clone = $hiddenData.clone().removeAttr('style').addClass('cloned-item');
                        const offset = _this.offset();
                        const width = _this.outerWidth();
                        const height = _this.outerHeight();

                        const overlapPerc = 15;
                        const overlap = (overlapPerc / 100) * width;
                        const newWidth = width + overlap * 2;
                        const newHeight = height + overlap * 2;


                        let left = offset.left - (newWidth - width) / 2;
                        let top = offset.top - (newHeight - height) / 2;

                        // Remove existing clones before creating a new one
                        jQuery('.cloned-item').remove();
                        $clone.css({
                            position: 'absolute',
                            left: `${left}px`,
                            top: `${top}px`,
                            width: `${newWidth}px`,
                            height: `${newHeight}px`,
                            'z-index': 990,
                            'border-radius': '12px',
                            background: 'rgba(0, 0, 0, 0.1)',
                            opacity: 0,
                            transition: 'opacity 0.3s ease, transform 0.3s ease',
                            transform: 'scale(0.7)',
                            'pointer-events': 'auto',
                        });

                        jQuery('body').append($clone);

                        // Add click handler for block images
                        $clone.find('.block-images').on('click', function (event) {
                            event.preventDefault();
                            const $anchor = jQuery(this).find('a');

                            if ($anchor.length > 0) {
                                const href = $anchor.attr('href');
                                if (href) {
                                    window.location.href = href;
                                }
                            }
                        });

                        // Delegate the 'watchlistUpdated' event 
                        $clone.on('watchlistUpdated', (event, data) => {
                            const button = $hiddenData.find('.watch-list-btn');

                            // Validate button exists
                            if (button.length === 0) return;

                            const action = data.action;

                            // Simplified button update logic
                            const isAdding = action === 'add';
                            button.toggleClass('in-watchlist', isAdding)
                                .find('i')
                                .removeClass(isAdding ? 'icon-plus' : 'icon-check-2')
                                .addClass(isAdding ? 'icon-check-2' : 'icon-plus');

                            if(isAdding) button.attr('data-bs-title', 'Retirer de À regarder plus tard')

                            /* button.attr('data-bs-title', isAdding
                                ? 'Remove from watchlist'
                                : 'Add to watchlist'); */
                            button.attr('data-action', isAdding ? 'remove' : 'add');
                        });

                        // Apply the hover effect transition
                        setTimeout(() => {
                            const transformStyles = {
                                opacity: 1,
                                transform: 'scale(0.9)'
                            };

                            // Adjust transform for first and last elements
                            if (_this.hasClass('first')) {
                                transformStyles.transform += isRtl
                                    ? ' translateX(-13.5%)'
                                    : ' translateX(13.5%)';
                            } else if (_this.hasClass('last')) {
                                transformStyles.transform += isRtl
                                    ? ' translateX(13.5%)'
                                    : ' translateX(-13.5%)';
                            }

                            $clone.css(transformStyles);
                        }, 500);

                        // Handle hover on the cloned card
                        $clone.hover(
                            function () {
                                clearTimeout(hoverTimeout); // Prevent removal while hovering
                            },
                            function () {
                                // Remove the cloned card when the mouse leaves
                                hoverTimeout = setTimeout(() => $clone.remove(), 10);
                            }
                        );
                    } catch (error) {
                        console.error('Error in hover effect:', error);
                    }
                }, 500); // Add a delay before triggering the hover effect
            },
            function () {
                clearTimeout(hoverDelayTimeout); // Cancel hover effect if mouse leaves
                const $clone = jQuery('.cloned-item');

                // Set a timeout to remove the cloned card
                hoverTimeout = setTimeout(() => {
                    if ($clone.length && !($clone[0].matches(':hover'))) {
                        $clone.remove();
                    }
                }, 10);
            }
        );
    }

    /**
     * Handles the event for fetching and displaying episodes for a selected season.
     * 
     * @param {Object} event - The event triggered by the user interaction.
     */
    GetSeasonEpisodes(event) {

        event.preventDefault();

        const btn = event.currentTarget;

        if (jQuery(btn).data('loaded')) return;

        jQuery(btn).data('loaded', true);

        // Accessing data attributes from the clicked button
        const season_no = jQuery(btn).data('season_no');
        const episode = jQuery(btn).data('episodes');
        const is_slider = jQuery(btn).data('is-slider');
        const result = is_slider === undefined ? true : (is_slider === false ? false : true);

        const targetId = jQuery(btn).attr("href");
        const target = jQuery(targetId);

        // Find the slider container and add a loading skeleton class
        const sliderTarget = target.find('.css_prefix-slick-general');
        sliderTarget.addClass('st-skeleton');

        // Get slider settings from the template (the first season slider)
        const slick_settings = jQuery('.css_prefix-slick-general:first').data('slider_settings');
        const extra_settings = jQuery('.css_prefix-slick-general:first').data('extra_settings');

        post('tvshow_tab_seasons_data', { data: episode, season: season_no, is_slider: result })

            .then(res => {
                try {
                    let json_res = JSON.parse(res);

                    // Check if the response indicates success
                    if (json_res['success'] && json_res['result'] && json_res['result'].length > 0) {

                        // If slider is already initialized, destroy it to be safe
                        if (sliderTarget.hasClass('slick-initialized')) sliderTarget.slick('unslick');

                        sliderTarget.empty().removeClass('st-skeleton');

                        // Populate the container with new slide items
                        json_res['result'].forEach((item) => {
                            const slideHTML = `<div class="slick-item"><div class="episode-card">${item['block_main_content']}</div></div>`;
                            sliderTarget.append(slideHTML);
                        });

                        // If it's a slider, initialize it
                        if (result) {
                            let settings = { ...slick_settings, rtl: document.documentElement.dir === 'rtl' };// Create a fresh copy of settings
                            if (extra_settings) {
                                settings.nextArrow = '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>';
                                settings.prevArrow = '<button class="PreArrow-two"><i class="icon-arrow-left"></i></button>';
                            }
                            sliderTarget.slick(settings);
                        } else {
                            // If it's not a slider, just show the content (already appended)
                            target.find('.css_prefix-episodes-content').html(sliderTarget.html());
                        }


                    } else {
                        // Handle case where no episodes are found
                        sliderTarget.empty().removeClass('st-skeleton');
                        sliderTarget.html('<p class="no_data_found">' + 'No Episode Available' + '</p>');
                    }
                } catch (e) {
                    console.error("Error parsing JSON response", e);
                }
            })
            .catch(err => {
                console.error("Error during AJAX request", err);
                sliderTarget.empty().removeClass('st-skeleton');
                sliderTarget.html('<p class="no_data_found">' + 'Error loading episodes.' + '</p>');
            });
    }

    /**
     * Initialize episode slider
     * @param {Event} e - Event
     */
    InitEpisodeSlider(e) {
        e.preventDefault();
        const current_target = jQuery(e.currentTarget);
        const targetId = jQuery('.init-episode-slider').attr("href");
        const season_no = current_target.data('season_no');
        const sliderSelector = jQuery(`.css_prefix-slick-general[data-display="${season_no}"]`);
        jQuery(sliderSelector).slick('destroy')
        jQuery(sliderSelector).slick({
            slidesToShow: 5,
            slidesToScroll: 1,
            infinite: false,
            arrows: true,
            dots: false,
            draggable: true,
            nextArrow: '<button class="NextArrow-two"><i class="icon-arrow-right"></i></button>',
            prevArrow: '<button class="PreArrow-two"><i class="icon-arrow-left"></i></button>',
            responsive: [
                { breakpoint: 1368, settings: { slidesToShow: 3 } },
                { breakpoint: 1025, settings: { slidesToShow: 3 } },
                { breakpoint: 993, settings: { slidesToShow: 2 } },
                { breakpoint: 577, settings: { slidesToShow: 2 } }
            ],
        });
    }

    /**
     * Handles the "Remove Continue Watching" button functionality.
     * Sends an AJAX request to remove a post from the continue watching list.
     * 
     * @param {Event} event - The click event on the "Remove Continue Watching" button.
     */
    RemoveContinueWatching(event) {
        // Get the clicked button element
        var button = event.currentTarget;
        // Retrieve the closest slick item (container) for the button
        const slickItem = button.closest('.slick-slide'); // Adjust the selector as per your slick item structure

        jQuery(button).tooltip('dispose');
        // Retrieve data attributes from the button (post type and post ID)
        var postType = button.dataset.postType || '';  // The type of the post being removed from the continue watching list
        var id = button.dataset.id || '';  // The ID of the post being removed

        // Prepare the data to be sent in the AJAX request
        var data = { post_type: postType, post_id: id };

        // Send an AJAX request to remove the post from the continue watching list
        post('remove_continue_watch', data)
            .then(res => {
                // Log the response from the server (for debugging or confirmation)
                slickItem.remove();
            })
            .catch(err => {
                // Log any errors that occur during the AJAX request
                console.log(err);
            });
    }
}

// Initialize the class
new Slick_General();